import { useLocalization } from 'src/utils/useLocalization';
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
} from 'react';
import {
    logout,
    MINOR_INACTIVITY_TIMEOUT,
} from '../../../../utils/session.helpers';

import './style.scss';

interface InactivityModalProps {
    open: boolean;
    handlerOpen: (isOpen: boolean) => void;
}

interface InactivityModalRef {
    startLogoutTimer: () => void;
}

const InactivityModal = forwardRef<InactivityModalRef, InactivityModalProps>(
    (props, ref) => {
        const { open, handlerOpen } = props;
        const { modal } = useLocalization();
        const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);

        const clearLogoutTimer = useCallback(() => {
            if (logoutTimeoutRef.current) {
                clearTimeout(logoutTimeoutRef.current);
                logoutTimeoutRef.current = null;
            }
        }, []);

        const startLogoutTimer = useCallback(() => {
            clearLogoutTimer();
            logoutTimeoutRef.current = setTimeout(() => {
                logout();
            }, MINOR_INACTIVITY_TIMEOUT);
        }, [clearLogoutTimer]);

        useImperativeHandle(ref, () => ({
            startLogoutTimer,
        }));

        useEffect(() => {
            if (open) {
                startLogoutTimer();
            } else {
                clearLogoutTimer();
            }
            return clearLogoutTimer;
        }, [open, startLogoutTimer, clearLogoutTimer]);

        const handleContinueSession = useCallback(() => {
            clearLogoutTimer();
            handlerOpen(false);
        }, [clearLogoutTimer, handlerOpen]);

        return (
            <div className={'inactivity-modal'}>
                <h5>{modal.LABEL_MODAL_INACTIVITY_TITLE}</h5>
                <p
                    dangerouslySetInnerHTML={{
                        __html: modal.LABEL_MODAL_INACTIVITY_CONTENT,
                    }}
                ></p>
                <div className={'u-mt-xlarge u-pb-small'}>
                    <button
                        className='c-btn radius-blue c-btn--secondary'
                        onClick={() => logout()}
                    >
                        <span className='c-btn__text'>
                            {modal.LABEL_MODAL_INACTIVITY_BTN_LOGOUT}
                        </span>
                    </button>
                    <button
                        className='c-btn bg-blue u-ml-small'
                        onClick={handleContinueSession}
                    >
                        <span className='c-btn__text'>
                            {modal.LABEL_MODAL_INACTIVITY_BTN_CONTINUE}
                        </span>
                    </button>
                </div>
            </div>
        );
    },
);

InactivityModal.displayName = 'InactivityModal';

export default InactivityModal;
